import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { fetchAssetsByTag } from './marketsSlice';
import { 
  Container, 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  Box,
  IconButton,
  Tooltip,
  Chip,
  Skeleton
} from '@mui/material';
import { 
  ArrowBack, 
  TrendingUp, 
  TrendingDown, 
  ShowChart,
  Tag as TagIcon
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import DWLFLoader from '../../components/DWLFLoader';

const TagPage = () => {
  const { tag } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const assetsByTag = useSelector((state) => state.markets.assetsByTag[tag] || []);
  const status = useSelector((state) => state.markets.status);
  const error = useSelector((state) => state.markets.error);

  useEffect(() => {
    if (tag) {
      dispatch(fetchAssetsByTag(tag));
    }
  }, [dispatch, tag]);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  // Loading skeleton component
  const AssetCardSkeleton = () => (
    <Card sx={{ 
      p: 3, 
      border: '1px solid', 
      borderColor: 'var(--dwlf-ink-10)',
      borderRadius: 2,
      background: 'var(--dwlf-ink-02)'
    }}>
      <CardContent>
        <Skeleton variant="text" width="60%" height={32} />
        <Skeleton variant="text" width="80%" height={20} sx={{ mt: 1 }} />
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
          <Skeleton variant="rectangular" width={80} height={24} sx={{ borderRadius: 1 }} />
        </Box>
      </CardContent>
    </Card>
  );

  // Enhanced asset card component
  const AssetCard = ({ asset }) => {
    const hasPerformanceData = asset.percentageChanges;
    const ytdChange = hasPerformanceData ? asset.percentageChanges.ytdChange : null;
    const latestChange = hasPerformanceData ? asset.percentageChanges.latestCandleChange : null;

    return (
      <motion.div variants={itemVariants} whileHover={{ scale: 1.02 }}>
        <Link 
          to={`/markets/${asset.symbol.replace('/', '-')}`} 
          style={{ textDecoration: 'none' }}
        >
          <Card
            sx={{
              p: 3,
              border: '1px solid',
              borderColor: 'var(--dwlf-ink-10)',
              borderRadius: 2,
              background: 'var(--dwlf-ink-02)',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'var(--dwlf-ink-20)',
                background: 'var(--dwlf-ink-05)',
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
              }
            }}
          >
            <CardContent sx={{ p: 0 }}>
              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Box>
                  <Typography 
                    variant="h6" 
                    fontWeight={700}
                    sx={{ 
                      color: 'text.primary',
                      mb: 0.5
                    }}
                  >
                    {asset.symbol}
                  </Typography>
                  <Typography 
                    variant="body2" 
                    color="text.secondary"
                    sx={{ fontSize: '0.875rem' }}
                  >
                    {asset.assetName}
                  </Typography>
                </Box>
                <ShowChart sx={{ color: 'primary.main', opacity: 0.7 }} />
              </Box>

              {/* Performance Metrics */}
              {hasPerformanceData && (
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {ytdChange !== null && (
                    <Chip
                      icon={ytdChange >= 0 ? <TrendingUp /> : <TrendingDown />}
                      label={`YTD ${ytdChange >= 0 ? '+' : ''}${ytdChange.toFixed(2)}%`}
                      size="small"
                      sx={{
                        backgroundColor: ytdChange >= 0 
                          ? 'rgba(76, 175, 80, 0.1)' 
                          : 'rgba(244, 67, 54, 0.1)',
                        color: ytdChange >= 0 ? '#4CAF50' : '#F44336',
                        border: `1px solid ${ytdChange >= 0 ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`,
                        '& .MuiChip-icon': {
                          color: ytdChange >= 0 ? '#4CAF50' : '#F44336'
                        }
                      }}
                    />
                  )}
                  {latestChange !== null && (
                    <Chip
                      icon={latestChange >= 0 ? <TrendingUp /> : <TrendingDown />}
                      label={`1D ${latestChange >= 0 ? '+' : ''}${latestChange.toFixed(2)}%`}
                      size="small"
                      sx={{
                        backgroundColor: latestChange >= 0 
                          ? 'rgba(76, 175, 80, 0.1)' 
                          : 'rgba(244, 67, 54, 0.1)',
                        color: latestChange >= 0 ? '#4CAF50' : '#F44336',
                        border: `1px solid ${latestChange >= 0 ? 'rgba(76, 175, 80, 0.3)' : 'rgba(244, 67, 54, 0.3)'}`,
                        '& .MuiChip-icon': {
                          color: latestChange >= 0 ? '#4CAF50' : '#F44336'
                        }
                      }}
                    />
                  )}
                </Box>
              )}
            </CardContent>
          </Card>
        </Link>
      </motion.div>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        {/* Header */}
        <motion.div variants={itemVariants}>
          <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            mb: 4,
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Tooltip title="Back to Markets">
                <IconButton 
                  onClick={() => navigate('/markets/overview')}
                  sx={{ 
                    backgroundColor: 'var(--dwlf-ink-05)',
                    color: 'text.secondary',
                    '&:hover': { 
                      backgroundColor: 'var(--dwlf-ink-10)',
                      color: 'text.primary'
                    }
                  }}
                >
                  <ArrowBack />
                </IconButton>
              </Tooltip>
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <TagIcon sx={{ color: 'primary.main' }} />
                  <Typography 
                    variant="h4" 
                    fontWeight={800} 
                    sx={{
                      color: 'var(--dwlf-accent)',
                    }}
                  >
                    {tag.charAt(0).toUpperCase() + tag.slice(1)} Assets
                  </Typography>
                </Box>
                <Typography variant="h6" color="text.secondary">
                  {status === 'succeeded' && `${assetsByTag.length} assets found`}
                </Typography>
              </Box>
            </Box>
          </Box>
        </motion.div>

        {/* Error State */}
        {status === "failed" && (
          <motion.div variants={itemVariants}>
            <Box sx={{ 
              textAlign: 'center', 
              py: 8,
              color: 'error.main'
            }}>
              <Typography variant="h6" gutterBottom>
                Error Loading Assets
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {error}
              </Typography>
            </Box>
          </motion.div>
        )}

        {/* Loading State */}
        {status === "loading" && (
          <Grid container spacing={3}>
            {[...Array(6)].map((_, index) => (
              <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
                <DWLFLoader size="medium" />
              </Grid>
            ))}
          </Grid>
        )}

        {/* Assets Grid */}
        {status === "succeeded" && (
          <>
            {assetsByTag.length > 0 ? (
              <Grid container spacing={3}>
                {assetsByTag.map((asset) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={asset.symbol}>
                    <AssetCard asset={asset} />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <motion.div variants={itemVariants}>
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 8,
                  color: 'text.secondary'
                }}>
                  <TagIcon sx={{ fontSize: 64, mb: 2, opacity: 0.3 }} />
                  <Typography variant="h6" gutterBottom>
                    No Assets Found
                  </Typography>
                  <Typography variant="body2">
                    No assets are currently tagged with "{tag}".
                  </Typography>
                </Box>
              </motion.div>
            )}
          </>
        )}
      </motion.div>
    </Container>
  );
};

export default TagPage;